import { Modal, Typography, theme } from "antd";
import PubSub from "pubsub-js";
import { useEffect, useState } from "react";
import { TMedia } from "../../models/media.model";
import { useTheme } from "../../providers/ThemeProvider";
import { useAuth } from "../../providers/UserProvider";
import { authWikipedia } from "../../services/integrations/wikipedia.service";
import { uploadMedia } from "../../services/media.service";
import {
  getUserProfile,
  updateUserProfile,
} from "../../services/users.service";
import { base64ToFile } from "../../utils/base64ToFile";
import { deepCopy } from "../../utils/deepCopy";
import { AccountSetupAvatar } from "./AccountSetupAvatar";
import { AccountSetupInterest } from "./AccountSetupInterest";
import { AccountSetupLanguage } from "./AccountSetupLanguage";
import { AccountSetupProfile } from "./AccountSetupProfile";
const { Text, Title, Link } = Typography;
const { useToken } = theme;

export function AccountSetup({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { message } = useTheme();
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({
    interests: [],
  });

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isOpen, setOpen] = useState<boolean>(true);
  const [isUpdating, setUpdating] = useState<boolean>(false);
  const steps = 4;

  // Load stored user profile
  useEffect(() => {
    async function updatePhoto() {
      if (user.photoURL) {
        const downloadPhoto = await fetch(user.photoURL);
        const blob = await downloadPhoto.blob();
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          setMetadata((prevState) => {
            const copy = deepCopy(prevState);

            copy.avatar = reader.result;

            return copy;
          });
        };
      }

      setMetadata({
        name: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ")[1] || "",
      });
    }

    updatePhoto();

    getUserProfile({
      onSuccess: (data) => {
        setMetadata({
          name: data.name,
          lastName: data.lastName,
          birthday: data.birthday,

          interests: data.interests,
          country: data.country,
          language: data.language,
          gender: data.gender,
        });
      },
    });
  }, [user]);

  return (
    <>
      <Modal
        open={isOpen}
        closeIcon={<></>}
        footer={() => <></>}
        onOk={() => {}}
        onCancel={() => {}}
      >
        {
          {
            0: (
              <AccountSetupAvatar
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                metadata={metadata}
                setMetadata={setMetadata}
              />
            ),
            1: (
              <AccountSetupProfile
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                metadata={metadata}
                setMetadata={setMetadata}
              />
            ),
            2: (
              <AccountSetupLanguage
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                metadata={metadata}
                setMetadata={setMetadata}
              />
            ),
            3: (
              <AccountSetupInterest
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                metadata={metadata}
                setMetadata={setMetadata}
                isUpdating={isUpdating}
                onComplete={async () => {
                  if (isUpdating) return;

                  setUpdating(true);

                  let media: TMedia;

                  // Upload user avatar
                  if (metadata.avatar) {
                    media = (await new Promise((resolve, reject) => {
                      const file = base64ToFile(metadata.avatar, "avatar.jpg");

                      uploadMedia({
                        file,
                        onSuccess: resolve,
                        onError: reject,
                      });
                    }).catch((err) => {
                      message.error("Failed to upload media");

                      return undefined;
                    })) as TMedia;
                  }

                  // Update user profile
                  const updatedProfile = await new Promise(
                    (resolve, reject) => {
                      let data: any = {
                        name: metadata.name,
                        lastName: metadata.lastName,
                        language: metadata.language,
                        birthday: metadata.birthday,
                        country: metadata.country,
                        gender: metadata.gender,
                        interests: metadata.interests,

                        isOnboardingDone: true,
                      };

                      if (media) {
                        data.avatar = media.id;
                      }

                      updateUserProfile({
                        data,
                        onSuccess: resolve,
                        onError: reject,
                      });
                    }
                  ).catch(() => {
                    message.error("Failed to update profile");
                  });

                  if (updatedProfile) {
                    PubSub.publish("profileUpdate", updatedProfile);
                  }

                  await new Promise((resolve) => {
                    authWikipedia({
                      onSuccess: () => resolve(true),
                      onError: () => resolve(true),
                    });
                  });

                  // Close onboarding modal
                  setOpen(false);
                  onClose();
                  setUpdating(false);

                  message.success("Profile updated!");
                }}
              />
            ),
          }[currentStep]
        }
      </Modal>
    </>
  );
}
